import React from 'react';
import {
    DiscountItem, WithId, DiscountUsage,
    Either, Left, Right,
} from 'widok-bridge';
import { FirestoreDb, useFirestoreDb } from '../Firebase/Db';
import { Loadable, TransformLoadables } from "../util/Loadable";
import {
    useLocation
} from 'react-router-dom';
import { LocalDateTime, Instant } from '@js-joda/core';

/**
 * Assumes search to be "?id=<sourceId>".
 * TODO(eryk): Migrate to use query-strings or react-router-dom v6.
 */
function extractDiscountId(query: string) {
    const match = query.match(/^\?id=(.*)$/i)
    if (match === null || (match?.length ?? 0) < 2) {
        return undefined;
    }
    return match[1];
}

function useDiscountId() {
    const { search } = useLocation()
    return extractDiscountId(search); // ?? '3V0EM5qYBEzGBP6CX6Ry';

}

export interface DiscountViewModel {
    discountItem: WithId<DiscountItem>;
    discountUsages: WithId<DiscountUsage>[];
    qrCodeValue: string;
}
export function useDiscountViewModel(): Loadable<DiscountViewModel> {
    const discountItem = useDiscountItem();
    const discountUsageList = useDiscountUsageListInternal();
    const selfLink: string = window.location.href ?? 'unknown';
    return TransformLoadables(
        TransformLoadables(discountItem, discountUsageList, calculateDiscountList),
        Loadable.finishLoading({ obj: selfLink }),
        (vm, qrCodeValue) => ({ ...vm, qrCodeValue })
    );
}

function calculateDiscountList(
    discountItem: WithId<DiscountItem>,
    discountUsages: WithId<DiscountUsage>[])
    : Pick<DiscountViewModel, 'discountItem' | 'discountUsages'> {
    return { discountItem, discountUsages };
}

export enum DiscountSearchErrorType {
    OK = 0,
    NO_ID = 1,
    SEARCH_NO_RESULTS = 2,
    SEARCH_FAILED = 3,
}

function useDiscountItem() {
    const [state, setState] =
        React.useState<Loadable<WithId<DiscountItem>>>(Loadable.empty());
    const fb = useFirestoreDb();
    const discountId = useDiscountId();
    const triggerLoading = React.useRef(true);

    const get = React.useCallback((fb: FirestoreDb, discountId: string | undefined) => {
        if (triggerLoading.current) {
            triggerLoading.current = false;
            const load = async (discountId: string) => {
                console.log(`Start loading discount ${discountId}`);
                setTimeout(() => setState(Loadable.startLoading()));
                try {
                    const discount = await loadDiscountById(fb, discountId);
                    setTimeout(() => setState(
                        discount.match(
                            obj => Loadable.finishLoading({ obj }),
                            error => Loadable.loadingErrorWithCustomId(
                                DiscountSearchErrorType.SEARCH_NO_RESULTS, error
                            ))));
                } catch (error) {
                    console.error(error);
                    setTimeout(() => setState(Loadable.loadingErrorWithCustomId(
                        DiscountSearchErrorType.SEARCH_FAILED, error
                    )));
                }
            };
            if (discountId === undefined) {
                console.log("Unknown discount id");
                setTimeout(() => setState(Loadable.loadingErrorWithCustomId(
                    DiscountSearchErrorType.NO_ID, new Error("Unknown source id"))));
            } else {
                load(discountId);
            }
        }
        return state;
    }, [state, setState]);

    return get(fb, discountId);
}

function useDiscountUsageListInternal(): Loadable<WithId<DiscountUsage>[]> {
    const [state, setState] =
        React.useState<Loadable<WithId<DiscountUsage>[]>>(Loadable.empty());
    const fb = useFirestoreDb();
    const discountId = useDiscountId();
    const triggerLoading = React.useRef(true);

    const get = React.useCallback((fb: FirestoreDb, discountId: string | undefined) => {
        if (triggerLoading.current) {
            triggerLoading.current = false;
            const load = async (sourceId: string) => {
                console.log(`Start loading discount usages for ${discountId}`);
                setTimeout(() => setState(Loadable.startLoading()));
                try {
                    const discounts = await loadDiscountUsagesInternal(fb, sourceId);
                    console.log(`Loaded ${discounts.length} discount usages`);
                    setTimeout(() => setState(Loadable.finishLoading({ obj: discounts })));
                } catch (error) {
                    console.error(error);
                    setTimeout(() => setState(Loadable.loadingError(error)));
                }
            };
            if (discountId === undefined) {
                console.log("Unknown discount id");
                setTimeout(() => setState(Loadable.loadingError(new Error("Unknown source id"))));
            } else {
                load(discountId);
            }

        }
        return state;
    }, [state, setState]);

    return get(fb, discountId);
}

async function loadDiscountUsagesInternal(fb: FirestoreDb, discountId: string): Promise<WithId<DiscountUsage>[]> {
    const query = fb.getDiscountUsageRootBySourceId(discountId);
    const result = await query.get();
    return result.docs.map(d => ({
        id: d.id,
        obj: fromSaveable(d.data()),
    }))
}

async function loadDiscountById(fb: FirestoreDb, discountId: string)
    : Promise<Either<WithId<DiscountItem>, Error>> {
    const doc = await fb.getDiscountsRoot().doc(discountId).get();
    const data = doc.exists && doc.data();
    if (!data) {
        return Right.of(new Error(`Unknown discount ${discountId}`));
    }
    return Left.of({ id: doc.id, obj: data.obj });
}


export type SaveableDiscountUsage =
    Omit<DiscountUsage, 'timestamp'> & { timestamp?: firebase.firestore.Timestamp };

function fromSaveable(discountUsage: SaveableDiscountUsage): DiscountUsage {
    return {
        ...(discountUsage as Omit<SaveableDiscountUsage, 'timestamp'>),
        ...((discountUsage.hasOwnProperty('timestamp') && discountUsage.timestamp !== undefined)
            ? {
                timestamp: LocalDateTime.ofInstant(Instant.ofEpochSecond(discountUsage.timestamp.seconds))
            }
            : {}),
    };
}